import React, { useState, useEffect } from 'react'
import { Button, Form, Row, Col, Divider, Space } from 'antd'
import {
  errorNotification,
  successNotification,
  carMakesUrl,
  carModelsUrl,
  customDutyBodyTypesUrl,
  fuelTypesUrl,
  driveTrainsUrl,
  originsUrl,
  conditionsUrl,
  getRequest,
  postRequest,
  customDutiesUrl
} from '../../config'
import BasicInput from '../../components/Forms/BasicInput'
import SelectInput from '../../components/Forms/SelectInput'
import PropTypes from 'prop-types'

// Currencies supported by the duty calculator (match the backend BankingExchangeRate).
const currencyOptions = ['USD', 'EUR', 'CNY', 'AED', 'CAD', 'JPY', 'KRW'].map(c => ({
  value: c,
  label: c
}))

const FormPage = props => {
  const [loading, setLoading] = useState(false)
  const [catLoading, setCatLoading] = useState(false)
  const [lookupLoading, setLookupLoading] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [serverErrors, setServerErrors] = useState({})

  const [payload, setPayload] = useState({
    customer: {
      phone: '',
      email: '',
      momoPhone: '',
      momoName: '',
      momoNetwork: ''
    },
    make: '',
    model: '',
    condition: '',
    fuelType: '',
    origin: '',
    bodyType: '',
    driveTrain: '',
    trim: '',
    carYear: new Date().getFullYear(),
    chassisNumber: '',
    engineSize: '',
    passengers: 5,
    isSpecialPurpose: false,
    msrpInUsd: 0,
    msrpCurrency: 'USD'
  })

  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [bodyTypes, setBodyTypes] = useState([])
  const [fuelTypes, setFuelTypes] = useState([])
  const [driveTrains, setDriveTrains] = useState([])
  const [origins, setOrigins] = useState([])
  const [conditions, setConditions] = useState([])

  const getYears = () => {
    const years = []
    for (let i = new Date().getFullYear() + 1; i >= 1960; i--) {
      years.push({ value: i, label: i })
    }
    return years
  }

  const fetchData = async (url, setter, label) => {
    const response = await getRequest(`${url}/all`, label, setCatLoading)
    if (response.success) {
      setter(
        response.data.map(item => ({
          value: item.id,
          label: item.name || item.country || item.country_name
        }))
      )
    }
  }

  useEffect(() => {
    fetchData(carMakesUrl, setMakes, 'makes')
    fetchData(customDutyBodyTypesUrl, setBodyTypes, 'bodyTypes')
    fetchData(fuelTypesUrl, setFuelTypes, 'fuelTypes')
    fetchData(driveTrainsUrl, setDriveTrains, 'driveTrains')
    fetchData(originsUrl, setOrigins, 'origins')
    fetchData(conditionsUrl, setConditions, 'conditions')
  }, [])

  useEffect(() => {
    if (payload.make) {
      const getModels = async () => {
        const response = await postRequest(
          `${carModelsUrl}/for-make`,
          { make: payload.make },
          'models',
          setCatLoading
        )
        if (response.success) {
          setModels(
            response.data.map(item => ({ value: item.id, label: item.name }))
          )
        }
      }
      getModels()
    }
  }, [payload.make])

  const handleInputChange = (field, isCustomer = false) => event => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value
    if (isCustomer) {
      setPayload({
        ...payload,
        customer: { ...payload.customer, [field]: value }
      })
    } else {
      setPayload({ ...payload, [field]: value })
    }
  }

  const handleSelect = field => value => {
    setPayload({ ...payload, [field]: value })
  }

  const handleLookupMSRP = async () => {
    if (!payload.make || !payload.model || !payload.carYear) {
      errorNotification('Please select Make, Model and Year first')
      return
    }
    const res = await postRequest(
      `${customDutiesUrl}/lookup-msrp/`,
      {
        make: payload.make,
        model: payload.model,
        carYear: payload.carYear,
        trim: payload.trim,
        engineSize: payload.engineSize,
        driveTrain: payload.driveTrain,
        origin: payload.origin,
        fuelType: payload.fuelType,
        chassisNumber: payload.chassisNumber
      },
      'MSRP lookup',
      setLookupLoading
    )

    if (res.success) {
      setPayload({
        ...payload,
        msrpInUsd: res.data.msrpInUsd,
        msrpCurrency: res.data.msrpCurrency || payload.msrpCurrency
      })
      successNotification('MSRP updated from database')
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    // MSRP may carry formatting (commas) or be cleared to ''. Strip non-numeric noise
    // and coerce to a finite number or null — never send '' / NaN, which the backend
    // DecimalField rejects, and never let "50,000" become NaN -> null.
    const msrp = Number(String(payload.msrpInUsd ?? '').replace(/[^0-9.-]/g, ''))
    const res = await postRequest(
      customDutiesUrl,
      { ...payload, msrpInUsd: Number.isFinite(msrp) ? msrp : null },
      'duty request',
      setLoading
    )
    if (res.success) {
      successNotification('Request created successfully')
      props.handleCancel()
    } else if (res.error.response) {
      setServerErrors(res.error.response.data)
    }
  }

  return (
    <Form layout="vertical">
      <Divider orientation="left">Customer Details</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Phone"
            value={payload.customer.phone}
            handleChange={handleInputChange('phone', true)}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Email"
            value={payload.customer.email}
            handleChange={handleInputChange('email', true)}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Momo Name"
            value={payload.customer.momoName}
            handleChange={handleInputChange('momoName', true)}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Momo Phone"
            value={payload.customer.momoPhone}
            handleChange={handleInputChange('momoPhone', true)}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Momo Network"
            value={payload.customer.momoNetwork}
            handleChange={handleInputChange('momoNetwork', true)}
          />
        </Col>
      </Row>

      <Divider orientation="left">Vehicle Details</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Make"
            value={payload.make}
            onChange={handleSelect('make')}
            optionList={makes}
            loading={catLoading}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Model"
            value={payload.model}
            onChange={handleSelect('model')}
            optionList={models}
            loading={catLoading}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Year"
            value={payload.carYear}
            onChange={handleSelect('carYear')}
            optionList={getYears()}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Trim"
            value={payload.trim}
            handleChange={handleInputChange('trim')}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Chassis Number"
            value={payload.chassisNumber}
            handleChange={handleInputChange('chassisNumber')}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Engine Size (L)"
            inputType="number"
            value={payload.engineSize}
            handleChange={handleInputChange('engineSize')}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Body Type"
            value={payload.bodyType}
            onChange={handleSelect('bodyType')}
            optionList={bodyTypes}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Fuel Type"
            value={payload.fuelType}
            onChange={handleSelect('fuelType')}
            optionList={fuelTypes}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Drive Train"
            value={payload.driveTrain}
            onChange={handleSelect('driveTrain')}
            optionList={driveTrains}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Origin"
            value={payload.origin}
            onChange={handleSelect('origin')}
            optionList={origins}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Condition"
            value={payload.condition}
            onChange={handleSelect('condition')}
            optionList={conditions}
            required
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Passengers"
            inputType="number"
            value={payload.passengers}
            handleChange={handleInputChange('passengers')}
          />
        </Col>
      </Row>
      <Row gutter={16} align="bottom">
        <Col span={6}>
          <BasicInput
            label="MSRP"
            inputType="number"
            value={payload.msrpInUsd}
            handleChange={handleInputChange('msrpInUsd')}
            required
          />
        </Col>
        <Col span={4}>
          <SelectInput
            label="Currency"
            value={payload.msrpCurrency}
            onChange={handleSelect('msrpCurrency')}
            optionList={currencyOptions}
          />
        </Col>
        <Col span={4}>
          <Button
            onClick={handleLookupMSRP}
            loading={lookupLoading}
            block
            style={{ marginBottom: '24px' }}
          >
            Lookup MSRP
          </Button>
        </Col>
        <Col span={8}>
          <Form.Item label="Special Purpose">
            <input
              type="checkbox"
              checked={payload.isSpecialPurpose}
              onChange={handleInputChange('isSpecialPurpose')}
            />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <Space>
          <Button onClick={props.handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Create Request
          </Button>
        </Space>
      </div>
    </Form>
  )
}

FormPage.propTypes = {
  handleCancel: PropTypes.func
}

export default FormPage
